import {
    faChartLine,
    faCog,
    faLifeRing,
    faPencil,
    faPlusCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import {
    DateRange1,
    KeywordFilterType,
} from "@/mk2/apps/dash/containers/SeoPerformance/FilterForm";
import { dashSeoPerformaceUrl } from "@/mk2/apps/dash/urls";
import { faqIndexFetchTrigger } from "@/mk2/apps/faq/containers/FaqIndex/FaqIndex.actions";
import articlesReducer from "@/mk2/apps/faq/containers/FaqIndex/FaqIndex.reducers";
import articlesSaga from "@/mk2/apps/faq/containers/FaqIndex/FaqIndex.sagas";
import {
    getFaqIndexArticlesState,
    getFaqIndexPageState,
} from "@/mk2/apps/faq/containers/FaqIndex/FaqIndex.selectors";
import { ArticleLinkEntity, ArticleLinkSchema } from "@/mk2/apps/faq/schemas";
import { faqIndexUrl } from "@/mk2/apps/faq/urls";
import { WIKI_MENU_MORE } from "@/mk2/apps/wiki/containers/Article/Article.constants";
import { articleWiki, articleWikiOG, breadcrumbsWiki } from "@/mk2/apps/wiki/structuredData";
import { wikiArticleCreateUrl, wikiArticleEditUrl } from "@/mk2/apps/wiki/urls";
import { wikiArticleUrl } from "@/mk2/apps/wiki/utils";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { MenuItem } from "@/mk2/components/menu/MenuItem";
import { AppName } from "@/mk2/containers/Application/Application";
import { Menu } from "@/mk2/containers/Menu/Menu";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import { memoizeOne } from "@/mk2/helpers/cache";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { BrandingProps, HalfpageProps, PageRouteProps } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { MenuSelection, UserEntity, WikiArticleEntity, WikiArticleSchema } from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    FOOTER_HELP_LINK,
    FORUM_POST_ADMINCLUB_MENU,
    WIKI_ARTICLE_ADD_NEW,
    WIKI_ARTICLE_EDIT,
} from "@/mk/autogenerated/translations/FaqIndexPage.76f02e45834bfe148b249a1946a74330";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";
import { adminclub_wiki_article_url } from "@/mk/urls/functions";

import FaqIndex from "./FaqIndex";

const fasCogLg: FontAwesomeIconProps = { icon: faCog, size: "lg" };

interface OwnProps {}

interface StateProps {
    article: WikiArticleEntity;
    articleSlug: string;
    articles: ArticleLinkEntity[];
    baseUrl: string;
    errorMessage: string;
    isMobile: boolean;
    loadingState: LoadingState;
    query: string;
    requestUser: UserEntity;
    requestUserPermissions: string[];
}

interface DispatchProps {
    onLoad(articleSlug: string, query: string);
    onWikiToggleMenuMore();
}

type RouteProps = PageRouteProps<{
    articleSlug: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    query: string;
}

export default class FaqIndexPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = parse(qs) || {};
        return {
            query: (qp.q as string) || "",
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const articleSlug = ownProps.routeParams.articleSlug;
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const query = parsedQuery.query;

            const pageState = getFaqIndexPageState(state);
            const articlesState = getFaqIndexArticlesState(state, articleSlug, query);
            const article: WikiArticleEntity = getDenormalizedEntity<WikiArticleEntity>(
                state,
                WikiArticleSchema,
                articlesState.articleId,
            );
            const articles: ArticleLinkEntity[] = getDenormalizedEntities<ArticleLinkEntity>(
                state,
                ArticleLinkSchema,
                articlesState.articlesIds,
            );

            return {
                article,
                articleSlug,
                articles,
                baseUrl: getRequestBaseUrl(state),
                errorMessage: articlesState.errorMessage,
                isMobile: getRequestDeviceMobile(state),
                loadingState: articlesState.loadingState,
                query,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: faqIndexFetchTrigger,
            onWikiToggleMenuMore: () => menuToggle(WIKI_MENU_MORE),
        };
    }

    public getMetaTitle(props: Props): string {
        return FOOTER_HELP_LINK;
    }

    public getMetaDescription(props: Props): string {
        const { article } = props;
        if (article) {
            // custom napisany SEO description, alebo to iste ako prvy odstavec clanku
            return article.seoDescription || getFirstParagraphsPlaintext(article.bodyAST);
        } else {
            // pouzi defaultny description v Page
            return null;
        }
    }

    public getMetaFaqRichSnippet(props: Props): string {
        const { article } = props;
        return article?.seoFaqRichSnippet;
    }

    public getStructuredData(props: Props): object[] {
        const { article, baseUrl } = props;
        if (article) {
            return [breadcrumbsWiki(baseUrl, article), articleWiki(baseUrl, article)];
        }
    }

    public getOpenGraphData(
        props: Props,
        baseUrl: string,
        currentUrl: string,
    ): { [key: string]: string } {
        const { article } = props;
        if (article) {
            return {
                ...super.getOpenGraphData(props, baseUrl, currentUrl),
                ...articleWikiOG(baseUrl, article),
            };
        }
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        const { article } = props;
        if (article) {
            return baseUrl + wikiArticleUrl(article);
        }
    }

    public render(props: Props): false | JSX.Element {
        return (
            <div>
                <FaqIndex {...props} />
            </div>
        );
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public hasHeaderBackButton(props: Props): boolean {
        // hlavny faq index ma len hamburger, sub-indexy vracaju cez back na hlavny index
        const { articleSlug } = props;
        return articleSlug !== "faq";
    }

    public getDefaultBackPath(props: Props): string {
        const { articleSlug } = props;
        return articleSlug !== "faq" ? url(faqIndexUrl, { articleSlug: "faq" }) : null;
    }

    public getBrandingProps(props: Props): BrandingProps {
        return null;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return null;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { article, isMobile, onWikiToggleMenuMore, requestUser, requestUserPermissions } =
            props;

        const isDeleted = article && article.deletedAt;
        if (isDeleted) {
            return null;
        }

        // na mobile pre neprihlasenych nezobrazujeme ziadne action buttons, ani menu
        // aby tam kvoli brandingu mohlo byt velke logo
        const isOptimizedForGoogleLands = isMobile && !isAuthenticated(requestUser);
        if (isOptimizedForGoogleLands) {
            return null;
        }

        const isAdmin = requestUserPermissions.indexOf("wiki.can_edit_wiki") >= 0;

        const hasCreate = article && isAdmin;
        const hasEdit = article && isAdmin;
        const hasAdminclub = article && isAdmin;
        const hasSeoStats = article && requestUserPermissions.indexOf("users.can_access_dash") >= 0;
        const menu =
            hasCreate || hasEdit || hasAdminclub || hasSeoStats ? (
                <Menu
                    key="menu"
                    menuId={WIKI_MENU_MORE}
                    trigger={
                        <Btn
                            type={BtnType.BlueOutline}
                            icon={fasCogLg}
                            onClick={onWikiToggleMenuMore}
                        />
                    }
                >
                    {hasCreate && (
                        <MenuItem
                            label={WIKI_ARTICLE_ADD_NEW}
                            icon={faPlusCircle}
                            link={wikiArticleCreateUrl}
                            isAdminUI
                        />
                    )}

                    {hasEdit && (
                        <MenuItem
                            label={WIKI_ARTICLE_EDIT}
                            icon={faPencil}
                            link={url(wikiArticleEditUrl, { articleId: article.id })}
                            isAdminUI
                        />
                    )}

                    {hasAdminclub && (
                        <MenuItem
                            label={FORUM_POST_ADMINCLUB_MENU}
                            icon={faLifeRing}
                            link={adminclub_wiki_article_url(article.id)}
                            asAnchor
                            isAdminUI
                        />
                    )}

                    {hasSeoStats && (
                        <MenuItem
                            label="SEO traffic"
                            icon={faChartLine}
                            link={url(
                                dashSeoPerformaceUrl,
                                {},
                                {
                                    range1: DateRange1[DateRange1.Past12Weeks],
                                    urls: `pageid:wiki-article-${article.id}`,
                                    keywords: KeywordFilterType[KeywordFilterType.NonBranded],
                                },
                            )}
                            asAnchor
                            linkTarget="_blank"
                            isAdminUI
                        />
                    )}
                </Menu>
            ) : null;

        return menu;
    }

    public getHeaderTitle(props: Props): string {
        return FOOTER_HELP_LINK;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: "faq",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.faq.faqIndex", articlesReducer);
        store.injectSaga("containers.faq.faqIndex", articlesSaga);
    }
}
